import React, { useCallback, useEffect, useState } from "react";
import { FaUsers } from "react-icons/fa";
import { SectionCard, MiniStat } from "../SectionCard";
import { Avatar } from "../../OrgUsageAnalytics/components/Avatar";
import { fetchOrgUsageDashboard, dateRangeParams } from "../api";
import { fmtN } from "../format";

const ACCENT = "#4f46e5";

function Sparkline({ points }) {
  const values = (points || []).map((p) => p.events || 0);
  if (!values.length || values.every((v) => v === 0)) return null;
  const max = Math.max(...values, 1);
  return (
    <div className="oo-spark" aria-hidden="true">
      {values.map((v, i) => (
        <div
          key={i}
          className="oo-spark-bar"
          style={{ height: `${Math.max(4, (v / max) * 100)}%` }}
          title={`${fmtN(v)} events`}
        />
      ))}
    </div>
  );
}

export default function UsageSection({ organizationId, orgName, env, days, onMetrics }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const json = await fetchOrgUsageDashboard(
        organizationId,
        dateRangeParams(days),
        env,
      );
      setData(json);
      const summary = json.summary || {};
      const adoptionPct =
        summary.totalMembers > 0
          ? Math.round((summary.activeUsers / summary.totalMembers) * 1000) / 10
          : 0;
      onMetrics?.({
        adoptionPct,
        activeUsers: summary.activeUsers,
        totalMembers: summary.totalMembers,
        loansAccessed: summary.totalLoansAccessed,
      });
    } catch (err) {
      setError(err.message || "Failed to load usage analytics");
    } finally {
      setLoading(false);
    }
  }, [organizationId, env, days, onMetrics]);

  useEffect(() => {
    load();
  }, [load]);

  const summary = data?.summary || {};
  const points = data?.usageOverTime?.points || [];
  const features = data?.featureBreakdown?.features || [];
  const topMembers = [...(data?.members?.members || [])]
    .sort((a, b) => (b.totalEvents || 0) - (a.totalEvents || 0))
    .slice(0, 4);
  const adoptionPct =
    summary.totalMembers > 0
      ? Math.round((summary.activeUsers / summary.totalMembers) * 1000) / 10
      : 0;

  return (
    <SectionCard
      title="Usage & Adoption"
      subtitle={`Activity across the last ${days} days`}
      icon={<FaUsers />}
      accent={ACCENT}
      loading={loading && !data}
      error={error}
      partial={Boolean(data?.partial)}
      onRetry={load}
      size="hero"
      link={{
        to: `/org-usage-analytics/${organizationId}`,
        label: "Open User Usage dashboard",
        state: { organizationName: orgName },
      }}
    >
      <div className="oo-hero-headline">
        <div className="oo-hero-pct" style={{ color: ACCENT }}>
          {adoptionPct}%
        </div>
        <div className="oo-hero-pct-meta">
          <span className="oo-hero-pct-label">team adoption</span>
          <span className="oo-hero-pct-sub">
            {fmtN(summary.activeUsers)} of {fmtN(summary.totalMembers)} members active
          </span>
        </div>
        {features[0] ? (
          <div className="oo-chip oo-hero-feature">
            Top feature: <strong>{features[0].feature}</strong> · {fmtN(features[0].count)}
          </div>
        ) : null}
      </div>

      <div className="oo-ministat-row">
        <MiniStat label="Active users" value={fmtN(summary.activeUsers)} accent={ACCENT} />
        <MiniStat label="Members" value={fmtN(summary.totalMembers)} />
        <MiniStat label="Loans accessed" value={fmtN(summary.totalLoansAccessed)} />
        <MiniStat label="Days tracked" value={fmtN(days)} />
      </div>

      <Sparkline points={points} />

      {topMembers.length ? (
        <div className="oo-list">
          {topMembers.map((m) => (
            <div className="oo-list-row" key={m.userId || m.email}>
              <Avatar name={m.name || m.email} seed={m.userId || m.email} size="sm" />
              <div className="oo-list-main">
                <div className="oo-list-name">{m.name || m.email || "—"}</div>
                <div className="oo-list-sub">{m.email}</div>
              </div>
              <div className="oo-list-val">{fmtN(m.totalEvents ?? 0)}</div>
            </div>
          ))}
        </div>
      ) : (
        <div className="oo-empty-hint">No member activity in this period.</div>
      )}
    </SectionCard>
  );
}
